<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { MAX_BOX_FOLDER_URLS } from '@/constants';
import BoxFolderDeleteModal from '@/components/admin/user-documents/box-folder-delete-modal.vue';
import { BoxFolder } from '@/types';

const router = useRouter();
type Props = {
  initBoxFolders?: BoxFolder[];
  isSubmitting: boolean;
};
const props = withDefaults(defineProps<Props>(), {
  initBoxFolders: () => [],
});
type Emits = {
  submit: [boxFolders: BoxFolder[]];
};
const emit = defineEmits<Emits>();

// フォームの状態管理
const folderForms = ref<
  {
    folder: BoxFolder;
    isOpenDeleteModal: boolean;
  }[]
>(
  props.initBoxFolders.map(folder => ({
    folder,
    isOpenDeleteModal: false,
  })),
);

// 初期化
watch(
  () => props.initBoxFolders,
  initBoxFolders => {
    folderForms.value = initBoxFolders.map(folder => ({
      folder,
      isOpenDeleteModal: false,
    }));
  },
);

// URLの追加フォーム
const addFormValue = ref('');
const addFolderForm = () => {
  folderForms.value.push({
    folder: { id: null, folder_url: addFormValue.value },
    isOpenDeleteModal: false,
  });
  addFormValue.value = '';
};
const isAddFormButtonDisabled = computed(
  () =>
    addFormValue.value === '' ||
    folderForms.value.some(
      form => form.folder.folder_url === addFormValue.value,
    ),
);
const isAddFormDisplayable = computed(
  () => folderForms.value.length < MAX_BOX_FOLDER_URLS,
);

// 削除モーダル
const openDeleteModal = (index: number) => {
  folderForms.value[index].isOpenDeleteModal = true;
};
const closeDeleteModal = () => {
  folderForms.value.forEach(form => (form.isOpenDeleteModal = false));
};
const deleteFolderForm = (index: number) => {
  folderForms.value.splice(index, 1);
  closeDeleteModal();
};

// 保存
const submit = () => {
  emit(
    'submit',
    folderForms.value.map(form => form.folder),
  );
};
const isSubmitButtonDisabled = computed(
  () => props.isSubmitting || folderForms.value.length === 0,
);
</script>

<template>
  <div class="box-folder-form">
    <div class="form-content">
      <div class="form-header">
        <div class="title">
          <div class="c-title-bold--m">BOXフォルダ</div>
          <div class="c-text-regular--s form-count">
            ・{{ folderForms.length }}件/{{ MAX_BOX_FOLDER_URLS }}件
          </div>
        </div>
      </div>
      <div class="notice-row c-text-regular--s">
        フォルダURLは正規化して保存されます
      </div>
      <div class="form-list">
        <div class="c-text-regular--s title">URL</div>
        <div class="c-text-regular--m">
          <div
            v-for="(folderForm, i) in folderForms"
            :key="i"
            class="form-item"
          >
            <input
              type="text"
              class="c-textInput"
              v-model="folderForm.folder.folder_url"
              placeholder="https://"
            />
            <button
              class="c-btn c-btn--auto c-btnOutline"
              @click="openDeleteModal(i)"
            >
              削除
            </button>
            <BoxFolderDeleteModal
              v-if="folderForm.isOpenDeleteModal"
              v-model:open="folderForm.isOpenDeleteModal"
              :url="folderForm.folder.folder_url"
              @submit="deleteFolderForm(i)"
            />
          </div>
          <div class="add-url-form" v-if="isAddFormDisplayable">
            <input
              type="text"
              class="c-textInput"
              v-model="addFormValue"
              placeholder="https://"
            />
            <button
              class="c-btn c-btn--auto c-btn--AnewsPrimary"
              :class="{ disabled: isAddFormButtonDisabled }"
              :disabled="isAddFormButtonDisabled"
              @click="addFolderForm"
            >
              追加
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="form-footer">
      <div class="form-footer-inner">
        <button
          class="c-btn c-btn--auto c-btn--AnewsPrimary"
          @click="submit"
          :disabled="isSubmitButtonDisabled"
          :class="{ disabled: isSubmitButtonDisabled }"
        >
          保存
        </button>
        <button
          class="c-btn c-btn--auto c-btnOutline cancel-button"
          @click="router.push({ name: 'boxFolders' })"
        >
          キャンセル
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.box-folder-form {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.form-content {
  box-sizing: border-box;
  width: 100%;
  max-width: calc(912px + 48px);
  margin: 0 auto;
  padding: 24px;
}
// 内容が短くても画面の最下部に置く
.form-footer {
  position: sticky;
  bottom: 0;
  margin-top: auto;
  padding: 16px 24px;
  background: $color-white;
  box-shadow: 0px 1px 5px rgba($color-gray1000, 0.25);
  .form-footer-inner {
    display: flex;
    gap: 8px;
    max-width: 912px;
    margin: 0 auto;
  }
}
.form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  .title {
    align-items: center;
    display: flex;
    gap: 4px;
    .form-count {
      color: $color-gray600;
    }
  }
}
.cancel-button {
  background-color: $color-white;
}
.notice-row {
  margin-bottom: 12px;
  color: $color-gray1000;
}
.form-list {
  background-color: $color-white;
  border: 1px solid $color-gray400;
  border-radius: 4px;
  padding: 16px;
  .title {
    padding: 8px 0;
    border-bottom: 1px solid $color-gray400;
  }
  .buttons {
    display: flex;
    justify-content: flex-start;
    padding-top: 16px;
    gap: 8px;
  }
}
.form-item {
  border-bottom: 1px solid $color-gray400;
  padding: 16px 0;
  display: flex;
  gap: 4px;
  button {
    white-space: nowrap;
  }
}
.add-url-form {
  display: flex;
  padding: 8px 0;
  gap: 4px;
  button {
    white-space: nowrap;
  }
}
</style>
